import { useEffect } from "react";
import { Modal } from "antd";
import { useTranslation } from "react-i18next";
import {
  AudioOutlined,
  BulbOutlined,
  CloseOutlined,
  CustomerServiceOutlined,
  LineChartOutlined,
  ScanOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import { useViewport } from "../../../hooks/useViewport";
import { modalStyles, modalWrapStyle } from "../../../utils/modalStyles";
import { SparklesIcon } from "./SparklesIcon";

interface AiIntegrationsModalProps {
  open: boolean;
  onClose: () => void;
}

/** Възможностите във витрината; `available` — вече работи в системата. */
const AI_FEATURES = [
  { key: "voice", Icon: AudioOutlined, available: true },
  { key: "forecast", Icon: LineChartOutlined, available: false },
  { key: "restock", Icon: ShoppingCartOutlined, available: false },
  { key: "menu", Icon: BulbOutlined, available: false },
  { key: "reservations", Icon: CustomerServiceOutlined, available: false },
  { key: "invoices", Icon: ScanOutlined, available: false },
] as const;

/**
 * „AI интеграции" от началния екран — витрина за клиента, не екран: какво
 * идва (и какво вече работи). Шелът е като общото „Очаквайте скоро"
 * (`ComingSoonModal` в `Modules.tsx`) — кехлибарено сияние, close чип, голяма
 * икона и кехлибарен CTA, плюс решетка с възможностите.
 */
export const AiIntegrationsModal = ({
  open,
  onClose,
}: AiIntegrationsModalProps) => {
  const { t } = useTranslation();
  const { isMobile, isNarrow, isTablet } = useViewport();
  const width = isMobile ? undefined : isTablet ? 520 : 560;

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      // preventDefault = „Esc е консумиран" за глобалната Esc-навигация
      // (SharedAccountGate) — само затваря модала.
      e.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      closable={false}
      destroyOnHidden
      width={width}
      styles={modalStyles}
      style={modalWrapStyle}
    >
      <div className="relative bg-surface">
        {/* Кехлибарено сияние отгоре и фина решетка, която избледнява —
            като hero панела на „Модули" */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[260px] bg-[radial-gradient(ellipse_75%_85%_at_50%_0%,var(--color-brand-tint)_0%,transparent_70%)]"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[220px] bg-[linear-gradient(var(--color-shade-2)_1px,transparent_1px),linear-gradient(90deg,var(--color-shade-2)_1px,transparent_1px)] bg-[size:32px_32px] [mask-image:radial-gradient(ellipse_60%_70%_at_50%_0%,#000_20%,transparent_75%)]"
        />

        <button
          type="button"
          onClick={onClose}
          aria-label={t("common.close")}
          className="absolute right-2.5 top-2.5 z-[2] grid h-[30px] w-[30px] cursor-pointer place-items-center rounded-lg border-0 bg-transparent text-brand-deep-ink transition-colors hover:bg-brand-tint"
        >
          <CloseOutlined className="text-sm" />
        </button>

        {/* Hero */}
        <div
          className={`relative flex flex-col items-center gap-3 text-center ${
            isNarrow ? "px-5 pb-5 pt-9" : "px-8 pb-6 pt-11"
          }`}
        >
          <div className="relative">
            <div className="grid h-[72px] w-[72px] place-items-center rounded-[20px] border border-brand-tint2 bg-[linear-gradient(135deg,var(--color-brand-tint)_0%,var(--color-brand-tint2)_100%)] text-brand-deep-ink shadow-[0_12px_30px_color-mix(in_srgb,var(--color-brand)_22%,transparent)]">
              <SparklesIcon className="text-[34px]" />
            </div>
            <SparklesIcon
              aria-hidden
              className="absolute -right-3 -top-2 text-[15px] text-brand"
            />
            <SparklesIcon
              aria-hidden
              className="absolute -bottom-1 -left-3.5 text-[11px] text-brand/60"
            />
          </div>

          <span className="mt-2 inline-flex w-fit items-center rounded-full border border-brand/20 bg-brand-tint px-[11px] py-[5px] text-[11px] font-bold uppercase tracking-[1.1px] text-brand-deep-ink">
            {t("modules.comingSoonKicker")}
          </span>

          <h2
            className={`m-0 font-bold leading-[1.15] tracking-[-0.8px] text-ink ${
              isNarrow ? "text-[26px]" : "text-[30px]"
            }`}
          >
            {t("modules.aiModal.title")}
          </h2>

          <p className="m-0 max-w-[400px] text-[14px] font-medium leading-normal text-body">
            {t("modules.aiModal.subtitle")}
          </p>
        </div>

        {/* Възможностите */}
        <div
          className={`relative grid gap-2.5 ${
            isMobile ? "grid-cols-1" : "grid-cols-2"
          } ${isNarrow ? "px-4" : "px-6"}`}
        >
          {AI_FEATURES.map(({ key, Icon, available }) => (
            <div
              key={key}
              className={`flex min-w-0 items-start gap-3 rounded-xl border p-3 text-left ${
                available
                  ? "border-ok-tint2 bg-ok-tint"
                  : "border-shade-2 bg-panel"
              }`}
            >
              <span
                className={`grid h-9 w-9 shrink-0 place-items-center rounded-[10px] text-[17px] ${
                  available
                    ? "bg-surface text-ok-deep"
                    : "bg-brand-tint text-brand-deep-ink"
                }`}
              >
                <Icon />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-[13.5px] font-bold leading-[1.25] text-ink">
                    {t(`modules.aiModal.features.${key}.title`)}
                  </span>
                  {available && (
                    <span className="rounded-full bg-surface px-[7px] py-[2px] text-[10px] font-bold uppercase tracking-[0.6px] text-ok-deep">
                      {t("modules.aiModal.available")}
                    </span>
                  )}
                </div>
                <p className="m-0 mt-1 text-[12px] leading-[1.45] text-body">
                  {t(`modules.aiModal.features.${key}.desc`)}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p
          className={`relative m-0 text-center text-[12.5px] font-medium leading-normal text-muted ${
            isNarrow ? "px-5 pt-4" : "px-8 pt-5"
          }`}
        >
          {t("modules.aiModal.footnote")}
        </p>

        <div className={isNarrow ? "px-4 pb-[18px] pt-4" : "px-6 pb-[22px] pt-4"}>
          <button
            type="button"
            onClick={onClose}
            className="h-[46px] w-full cursor-pointer rounded-xl border-0 bg-brand text-[14px] font-bold tracking-[-0.2px] text-white transition-colors hover:bg-brand-deep"
          >
            {t("modules.comingSoonCta")}
          </button>
        </div>
      </div>
    </Modal>
  );
};
